import React from 'react';
import { EmptyState } from './EmptyState';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.props.onError?.(error, info);
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-[8px] p-5">
          <EmptyState
            variant="error"
            title="Component error"
            description={this.state.error?.message ?? 'An unexpected error occurred in this component.'}
            action={{ label: 'Try again', onClick: this.handleReset }}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
